import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Box,
	Typography,
	Button,
	TextField,
	Tooltip,
	Paper,
	Chip,
	IconButton,
	useTheme,
	useMediaQuery,
	CircularProgress,
	ToggleButton,
	ToggleButtonGroup,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from '@mui/material';
import { Add, Edit, Delete, NotificationsOutlined, Visibility } from '@mui/icons-material';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import SmsOutlined from '@mui/icons-material/SmsOutlined';
import WhatsApp from '@mui/icons-material/WhatsApp';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import FilterList from '@mui/icons-material/FilterList';
import MenuIcon from '@mui/icons-material/Menu';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { DataTableWithStaticSchema } from 'components/DataTable';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import {
	NotificationDialog,
	UserNotificationRecord,
} from './NotificationDialog';
import { NotificationViewDialog } from './NotificationViewDialog';

const VIEWMODE_STORAGE_KEY = 'settings-notifications-view-mode';
const TILE_PAGE_SIZE = 12;

const CHANNEL_LABELS: Record<string, string> = {
	email: 'Email',
	gsm: 'GSM Text',
	whatsapp: 'WhatsApp',
};

/** Channel badge styles aligned with System Templates (SMS = purple). */
const channelChipSx = (channel: string) => {
	const base = {
		height: 22,
		fontSize: '0.68rem',
		fontWeight: 700,
		color: '#fff',
		boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
	};
	switch (channel) {
		case 'gsm':
			return {
				...base,
				background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
			};
		case 'whatsapp':
			return { ...base, bgcolor: 'success.main' };
		case 'email':
			return { ...base, bgcolor: 'warning.main' };
		default:
			return { ...base, bgcolor: 'info.main' };
	}
};

const channelIcon = (channel: string) => {
	switch (channel) {
		case 'email':
			return <EmailOutlined sx={{ fontSize: 40, opacity: 0.85 }} />;
		case 'gsm':
			return <SmsOutlined sx={{ fontSize: 40, opacity: 0.85 }} />;
		case 'whatsapp':
			return <WhatsApp sx={{ fontSize: 40, opacity: 0.85 }} />;
		default:
			return <NotificationsOutlined sx={{ fontSize: 40, opacity: 0.85 }} />;
	}
};

const tableSchema = {
	paths: {
		_id: { instance: 'String', isRequired: false },
		name: { instance: 'String', isRequired: true },
		eventType: { instance: 'String', isRequired: true },
		channel: { instance: 'String', isRequired: true },
		status: { instance: 'String', isRequired: true },
		updatedAt: { instance: 'Date', isRequired: false },
	},
	obj: {},
};

export const Notifications: React.FC = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const [viewMode, setViewMode] = useState<'tile' | 'table'>(() => {
		const stored = localStorage.getItem(VIEWMODE_STORAGE_KEY);
		return stored === 'table' ? 'table' : 'tile';
	});

	const [notifications, setNotifications] = useState<UserNotificationRecord[]>([]);
	const [loading, setLoading] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [statusFilter, setStatusFilter] = useState<string>('all');
	const [refreshKey, setRefreshKey] = useState(0);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingNotification, setEditingNotification] = useState<UserNotificationRecord | null>(null);
	const [viewDialogOpen, setViewDialogOpen] = useState(false);
	const [viewingNotification, setViewingNotification] = useState<UserNotificationRecord | null>(null);

	const [tileVisibleCount, setTileVisibleCount] = useState(TILE_PAGE_SIZE);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [toolbarMenuOpen, setToolbarMenuOpen] = useState(false);

	const fetchNotifications = useCallback(async () => {
		setLoading(true);
		try {
			let endpoint = '/user-notifications/all';
			if (statusFilter !== 'all') {
				endpoint += `?status=${statusFilter}`;
			}
			const response = await apiClient.get(endpoint);
			setNotifications(Array.isArray(response.data) ? response.data : []);
		} catch (error) {
			Logger.error('Notifications', `Failed to fetch notifications: ${error}`);
			setNotifications([]);
		} finally {
			setLoading(false);
		}
	}, [statusFilter]);

	useEffect(() => {
		fetchNotifications();
	}, [fetchNotifications]);

	const filteredNotifications = useMemo(() => {
		if (!searchQuery.trim()) return notifications;
		const q = searchQuery.toLowerCase();
		return notifications.filter(
			(n) =>
				n.name.toLowerCase().includes(q)
				|| n.eventType.toLowerCase().includes(q)
				|| n.channel.toLowerCase().includes(q)
				|| (n.message || '').toLowerCase().includes(q),
		);
	}, [notifications, searchQuery]);

	useEffect(() => {
		setTileVisibleCount(TILE_PAGE_SIZE);
	}, [searchQuery, statusFilter, notifications]);

	const paginatedNotifications = useMemo(
		() => filteredNotifications.slice(0, tileVisibleCount),
		[filteredNotifications, tileVisibleCount],
	);
	const hasMoreTiles = tileVisibleCount < filteredNotifications.length;

	const handleCreate = () => {
		setEditingNotification(null);
		setDialogOpen(true);
	};

	const handleView = (notification: UserNotificationRecord) => {
		setViewingNotification(notification);
		setViewDialogOpen(true);
	};

	const handleEdit = (notification: UserNotificationRecord) => {
		setEditingNotification(notification);
		setDialogOpen(true);
	};

	const handleDelete = async (notification: UserNotificationRecord) => {
		if (!window.confirm(`Delete notification "${notification.name}"?`)) return;
		try {
			await apiClient.delete(`/user-notifications/${notification._id}`);
			setRefreshKey((k) => k + 1);
			fetchNotifications();
		} catch (error) {
			Logger.error('Notifications', `Failed to delete notification: ${error}`);
		}
	};

	const handleDialogClose = () => {
		setDialogOpen(false);
		setEditingNotification(null);
	};

	const handleDialogSave = () => {
		setDialogOpen(false);
		setEditingNotification(null);
		setRefreshKey((k) => k + 1);
		fetchNotifications();
	};

	const customActions = useCallback((record: any) => {
		return [{
			label: 'View Details',
			onClick: () => handleView(record),
			color: 'info' as const,
			icon: <Visibility />,
		}];
	}, []);

	const formatUpdatedAt = (value?: string) => {
		if (!value) return '—';
		try {
			return new Date(value).toLocaleDateString();
		} catch {
			return value;
		}
	};

	const channelLabel = (channel: string) => CHANNEL_LABELS[channel] || channel;

	const heroTileFg = theme.palette.dashboard?.heroTileForegroundColor;
	const heroTileLabelColor = theme.palette.dashboard?.heroTileLabelColor
		?? (heroTileFg ? 'rgba(0, 0, 0, 0.65)' : 'rgba(255,255,255,0.7)');
	const heroTileTextShadow = heroTileFg ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
	const tileGradient = theme.palette.dashboard?.counterHeroGradient
		?? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`;

	const renderNotificationTile = (notification: UserNotificationRecord) => (
		<Box
			key={notification._id}
			onClick={() => handleView(notification)}
			sx={{
				width: 260,
				height: 200,
				borderRadius: 2.5,
				overflow: 'hidden',
				position: 'relative',
				boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
				flexShrink: 0,
				border: '1px solid',
				borderColor: 'divider',
				cursor: 'pointer',
				'&:hover .tile-overlay': { height: 120 },
				'&:hover .tile-details': { opacity: 1 },
				'&:hover .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
				'@media (hover: none)': {
					'& .tile-overlay': { height: 120 },
					'& .tile-details': { opacity: 1 },
					'& .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
				},
			}}
		>
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					background: tileGradient,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					gap: 0.5,
					color: heroTileFg ?? '#fff',
				}}
			>
				{channelIcon(notification.channel)}
				<Typography
					sx={{
						fontSize: '0.9rem',
						fontWeight: 700,
						color: heroTileFg ?? '#fff',
						lineHeight: 1,
						textShadow: heroTileTextShadow,
						textAlign: 'center',
						px: 1,
						wordBreak: 'break-word',
					}}
				>
					{channelLabel(notification.channel)}
				</Typography>
				<Typography
					sx={{
						fontSize: '0.7rem',
						fontWeight: 600,
						color: heroTileLabelColor,
						textTransform: 'uppercase',
						letterSpacing: 1,
					}}
				>
					Channel
				</Typography>
			</Box>

			<Box sx={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 2, display: 'flex', gap: 0.5 }}>
				<Chip
					label={notification.status}
					color={notification.status === 'active' ? 'success' : 'warning'}
					size="small"
					sx={{
						height: 22,
						fontSize: '0.68rem',
						fontWeight: 700,
						color: '#fff',
						boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
						textTransform: 'capitalize',
					}}
				/>
				<Chip
					label={channelLabel(notification.channel)}
					size="small"
					sx={channelChipSx(notification.channel)}
				/>
			</Box>

			<Box
				className="tile-overlay"
				sx={{
					position: 'absolute',
					bottom: 0,
					left: 0,
					right: 0,
					overflow: 'hidden',
					background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.25) 100%)',
					transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
					height: 40,
					px: 1.5,
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'flex-start',
				}}
			>
				<Typography
					variant="subtitle2"
					noWrap
					className="tile-name-short"
					sx={{
						color: '#fff',
						fontWeight: 700,
						fontSize: '0.82rem',
						lineHeight: '40px',
						textShadow: '0 1px 3px rgba(0,0,0,0.4)',
						flexShrink: 0,
						transition: 'opacity 0.2s ease, height 0.2s ease, line-height 0.2s ease',
					}}
				>
					{notification.name}
				</Typography>

				<Box
					className="tile-details"
					sx={{
						opacity: 0,
						transition: 'opacity 0.3s ease 0.08s',
						display: 'flex',
						flexDirection: 'column',
						gap: 0.5,
						pt: 0.25,
						pb: 1,
						flex: 1,
					}}
				>
					<Typography
						variant="subtitle2"
						sx={{
							color: '#fff',
							fontWeight: 700,
							fontSize: '0.78rem',
							lineHeight: 1.3,
							textShadow: '0 1px 3px rgba(0,0,0,0.4)',
							wordBreak: 'break-word',
						}}
					>
						{notification.name}
					</Typography>
					<Typography
						variant="caption"
						sx={{
							color: 'rgba(255,255,255,0.7)',
							fontSize: '0.68rem',
							lineHeight: 1.2,
							wordBreak: 'break-all',
						}}
					>
						{notification.eventType}
					</Typography>
					{notification.updatedAt && (
						<Typography
							variant="caption"
							sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}
						>
							Updated: {formatUpdatedAt(notification.updatedAt)}
						</Typography>
					)}
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
						<Box display="flex" gap={0.5}>
							<Tooltip title="Edit">
								<Button
									color="secondary"
									variant="contained"
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										handleEdit(notification);
									}}
									sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
								>
									<Edit fontSize="small" />
								</Button>
							</Tooltip>
						</Box>
						<Box display="flex" gap={0.5}>
							<Tooltip title="Delete">
								<Button
									color="error"
									variant="contained"
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										handleDelete(notification);
									}}
									sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
								>
									<Delete fontSize="small" />
								</Button>
							</Tooltip>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);

	const renderTileView = () => (
		<Box display="flex" flexDirection="column" gap={2.5}>
			<Box
				display="flex"
				flexWrap="wrap"
				gap={2.5}
				sx={{ justifyContent: { xs: 'center', lg: 'flex-start' } }}
			>
				{paginatedNotifications.map((notification) => renderNotificationTile(notification))}
			</Box>

			{filteredNotifications.length === 0 && !loading && (
				<Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
					<Typography color="text.secondary">
						{searchQuery || statusFilter !== 'all'
							? 'No notifications match your filters.'
							: 'No notifications found. Create one to get started.'}
					</Typography>
				</Paper>
			)}

			{filteredNotifications.length > 0 && (
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pt: 1 }}>
					<Typography variant="body2" color="text.secondary">
						Showing {paginatedNotifications.length} of {filteredNotifications.length} notification
						{filteredNotifications.length !== 1 ? 's' : ''}
					</Typography>
					{hasMoreTiles && (
						<Button
							variant="outlined"
							color="secondary"
							onClick={() => setTileVisibleCount((prev) => prev + TILE_PAGE_SIZE)}
						>
							Load More
						</Button>
					)}
				</Box>
			)}
		</Box>
	);

	const tableDataUrl = useMemo(() => {
		let endpoint = '/user-notifications/all';
		if (statusFilter !== 'all') endpoint += `?status=${statusFilter}`;
		return endpoint;
	}, [statusFilter]);

	const renderTableView = () => (
		<DataTableWithStaticSchema
			dataUrl={tableDataUrl}
			schemaObject={tableSchema}
			columnsToExclude={['_id']}
			refreshTrigger={refreshKey}
			externalSearchQuery={searchQuery}
			hideSearch
			onEdit={(record: any) => handleEdit(record)}
			onDelete={(record: any) => handleDelete(record)}
			customActions={customActions}
			columnRenderers={{
				channel: (row: any) => (
					<Chip
						label={channelLabel(row.channel)}
						size="small"
						sx={channelChipSx(row.channel)}
					/>
				),
				status: (row: any) => (
					<Chip
						label={row.status}
						size="small"
						color={row.status === 'active' ? 'success' : 'warning'}
						sx={{
							height: 22,
							fontSize: '0.68rem',
							fontWeight: 700,
							color: '#fff',
							textTransform: 'capitalize',
						}}
					/>
				),
			}}
		/>
	);

	const toolbarBgSx = {
		backgroundColor: (t: any) =>
			t.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
	};

	const statusSelect = (
		<FormControl size="small" sx={{ minWidth: 130 }} fullWidth={isMobile && (mobileMenuOpen || toolbarMenuOpen)}>
			<InputLabel>Status</InputLabel>
			<Select
				value={statusFilter}
				label="Status"
				onChange={(e) => setStatusFilter(e.target.value as string)}
			>
				<MenuItem value="active">Active</MenuItem>
				<MenuItem value="inactive">Inactive</MenuItem>
				<MenuItem value="all">All</MenuItem>
			</Select>
		</FormControl>
	);

	const viewToggle = (
		<ToggleButtonGroup
			value={viewMode}
			exclusive
			onChange={(_e, newMode) => {
				if (newMode !== null) {
					setViewMode(newMode);
					localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
				}
			}}
			size="small"
			sx={{ '& .MuiToggleButton-root': { border: '1px solid', borderColor: 'divider' } }}
		>
			<ToggleButton value="tile" aria-label="Tile view">
				<Tooltip title="Tile View">
					<ViewModuleIcon fontSize="small" />
				</Tooltip>
			</ToggleButton>
			<ToggleButton value="table" aria-label="Table view">
				<Tooltip title="Table View">
					<TableRowsIcon fontSize="small" />
				</Tooltip>
			</ToggleButton>
		</ToggleButtonGroup>
	);

	return (
		<Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
			<DynamicBreadcrumbs
				items={[
					{ itemPath: '/dashboard', itemLabel: 'Settings' },
					{ itemPath: '/settings/notifications', itemLabel: 'Notifications' },
				]}
			/>

			{/* Toolbar — search, status, view toggle, New Notification (no Receivers / Default Worker Pool) */}
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					gap: { xs: 1, md: 2 },
					alignItems: 'center',
					mb: 3,
					p: 1.5,
					borderRadius: 2,
					...toolbarBgSx,
				}}
			>
				<Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
					<IconButton
						color="secondary"
						onClick={() => setMobileMenuOpen(true)}
						size="large"
						sx={{
							border: '1px solid',
							borderColor: 'secondary.main',
							'&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' },
						}}
					>
						<FilterList />
					</IconButton>
				</Box>
				<Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', ml: 'auto' }}>
					<IconButton
						color="secondary"
						onClick={() => setToolbarMenuOpen(true)}
						size="large"
						sx={{
							border: '1px solid',
							borderColor: 'secondary.main',
							'&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' },
						}}
					>
						<MenuIcon />
					</IconButton>
				</Box>

				<Box
					sx={{
						display: { xs: 'none', md: 'flex' },
						flexGrow: 1,
						flexWrap: 'nowrap',
						alignItems: 'center',
						gap: 1,
					}}
				>
					<TextField
						placeholder="Search Notification Type"
						label="Search Notification Type"
						variant="outlined"
						size="small"
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						sx={{ flexGrow: 1, minWidth: '220px', flexShrink: 1 }}
					/>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							flexWrap: 'nowrap',
							flexShrink: 0,
							ml: 'auto',
						}}
					>
						{statusSelect}
						{viewToggle}
						<Button
							variant="outlined"
							color="secondary"
							startIcon={<Add />}
							onClick={handleCreate}
							size="small"
							sx={{ whiteSpace: 'nowrap' }}
						>
							New Notification
						</Button>
					</Box>
				</Box>
			</Box>

			{/* Mobile: filters dialog */}
			<Dialog open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} fullWidth maxWidth="xs">
				<DialogTitle>Search &amp; Filters</DialogTitle>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} pt={1}>
						<TextField
							placeholder="Search Notification Type"
							label="Search Notification Type"
							variant="outlined"
							size="small"
							fullWidth
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>
						{statusSelect}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setMobileMenuOpen(false)}>Done</Button>
				</DialogActions>
			</Dialog>

			{/* Mobile: toolbar actions dialog */}
			<Dialog open={toolbarMenuOpen} onClose={() => setToolbarMenuOpen(false)} fullWidth maxWidth="xs">
				<DialogTitle>Actions</DialogTitle>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} pt={1} alignItems="stretch">
						<Box display="flex" justifyContent="center">
							{viewToggle}
						</Box>
						<Button
							variant="outlined"
							color="secondary"
							startIcon={<Add />}
							onClick={() => {
								setToolbarMenuOpen(false);
								handleCreate();
							}}
							fullWidth
						>
							New Notification
						</Button>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setToolbarMenuOpen(false)}>Close</Button>
				</DialogActions>
			</Dialog>

			{loading && notifications.length === 0 ? (
				<Box display="flex" justifyContent="center" p={4}>
					<CircularProgress color="secondary" />
				</Box>
			) : viewMode === 'tile' ? (
				renderTileView()
			) : (
				renderTableView()
			)}

			{dialogOpen && (
				<NotificationDialog
					open={dialogOpen}
					onClose={handleDialogClose}
					onSave={handleDialogSave}
					notification={editingNotification}
				/>
			)}

			<NotificationViewDialog
				open={viewDialogOpen}
				onClose={() => {
					setViewDialogOpen(false);
					setViewingNotification(null);
				}}
				notification={viewingNotification}
			/>
		</Box>
	);
};

export default Notifications;
